import { useEffect, useState } from 'react';
import { type LayoutChangeEvent, Pressable, ScrollView } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { Text, XStack } from 'tamagui';
import { useThemeColors } from '@kiips/ui';

interface TabSegmentProps {
  tabs: string[];
  active: number;
  onChange: (next: number) => void;
  /** Stretch the track across the available width instead of hugging
   *  its labels. Tabs keep their natural proportions and share the
   *  leftover space, so a long label ("Placeholder") still fits while
   *  short ones simply get roomier. Opt-in: the centred, content-width
   *  form is right for tabs floating over a hero. */
  fullWidth?: boolean | undefined;
}

/**
 * N-tab pill switcher. A single absolutely-positioned active-background
 * `Animated.View` slides between tab x-positions on change via
 * `withSpring`; the labels themselves are static `<Text>`s rendered
 * over the background. Inactive labels are slightly desaturated.
 */
export function TabSegment({ tabs, active, onChange, fullWidth }: TabSegmentProps) {
  const theme = useThemeColors();
  const [layouts, setLayouts] = useState<Array<{ x: number; width: number } | null>>(() => tabs.map(() => null));
  const indicatorX = useSharedValue(0);
  const indicatorW = useSharedValue(0);

  useEffect(() => {
    const layout = layouts[active];
    if (!layout) return;
    indicatorX.value = withSpring(layout.x, { damping: 30, stiffness: 300, overshootClamping: true });
    indicatorW.value = withSpring(layout.width, { damping: 30, stiffness: 300, overshootClamping: true });
  }, [active, layouts, indicatorX, indicatorW]);

  const indicatorStyle = useAnimatedStyle(() => ({
    left: indicatorX.value,
    width: indicatorW.value,
  }));

  return (
    // Horizontally scrollable so a wide tab set — four tabs, or any
    // count under OS text scaling — degrades to a scroll rather than
    // silently clipping the trailing tab off the rounded track. The
    // row still sizes to its content, so short tab sets are unchanged
    // and simply never scroll.
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      alwaysBounceHorizontal={false}
      contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
      style={fullWidth ? { flexGrow: 0, alignSelf: 'stretch' } : { flexGrow: 0 }}
    >
    <XStack
      alignSelf={fullWidth ? 'stretch' : 'center'}
      flex={fullWidth ? 1 : undefined}
      backgroundColor="$borderMuted"
      borderRadius={18}
      padding={4}
      gap={2}
      overflow="hidden"
    >
      <Animated.View
        style={[
          {
            position: 'absolute',
            top: 4,
            bottom: 4,
            left: 0,
            backgroundColor: theme.textOnSurface,
            borderRadius: 14,
          },
          indicatorStyle,
        ]}
      />
      {tabs.map((label, i) => (
        <Pressable
          key={label}
          onPress={() => onChange(i)}
          accessibilityRole="tab"
          accessibilityLabel={label}
          accessibilityState={{ selected: i === active }}
          onLayout={(e: LayoutChangeEvent) => {
            const { x, width } = e.nativeEvent.layout;
            setLayouts((prev) => {
              const next = prev.slice();
              next[i] = { x, width };
              return next;
            });
          }}
          style={{
            paddingHorizontal: 12,
            paddingVertical: 8,
            borderRadius: 14,
            // flexGrow shares the leftover width equally *on top of*
            // each tab's natural size, so the long label keeps the room
            // it needs rather than every tab collapsing to width/N.
            ...(fullWidth ? { flexGrow: 1, flexBasis: 'auto' as const, alignItems: 'center' as const } : null),
          }}
        >
          <Text
            fontSize={13}
            fontWeight="600"
            numberOfLines={1}
            // Active label sits on the JS-resolved `theme.textOnSurface`
            // pill, so colour it from the same system — a `$textInverse`
            // CSS token resolves before the JS pill on web hydration and
            // flashes dark-on-dark. Inactive sits on the `$borderMuted`
            // CSS track, so it stays a CSS token.
            color={i === active ? theme.textInverse : '$textSubdued'}
          >
            {label}
          </Text>
        </Pressable>
      ))}
    </XStack>
    </ScrollView>
  );
}
